import {
	Button,
	Col,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Row,
	Select,
	Space,
	Table,
	Tag,
	Tooltip,
	Typography,
} from "antd";
import {
	CheckOutlined,
	CloseOutlined,
	DeleteOutlined,
	EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
	advancePaymentUpdateAction,
	loadAdvancePaymentStatusMaster,
	loadAdvancePaymentsById,
	loadAdvancePaymentsByReporting,
	saveAdvancePayment,
} from "../../redux/rtk/features/advancePayment/advancePaymentSlice";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import getUserFromToken from "../../utils/getUserFromToken";

const { Title } = Typography;
const DATE_FORMAT = "YYYY-MM-DD";

const getStatusLabel = (value, options = []) =>
	options.find((option) => option.value === Number(value))?.label ?? "Pending";

const statusStringToId = (status, statusNameToIdMap = new Map()) => {
	const normalized = (status ?? "").toString().toUpperCase();
	const matchedStatusId = statusNameToIdMap.get(normalized);
	if (matchedStatusId !== undefined) {
		return matchedStatusId;
	}
	if (normalized === "APPROVED" || normalized === "ACCEPTED") {
		return 1;
	}
	if (normalized === "REJECTED") {
		return 0;
	}
	if (normalized === "PENDING") {
		return 3;
	}
	return 0;
};

const getStatusColor = (statusLabel) => {
	const normalized = (statusLabel ?? "").toString().trim().toUpperCase();
	if (normalized === "APPROVED" || normalized === "ACCEPTED") return "success";
	if (normalized === "REJECTED") return "error";
	if (normalized === "PENDING") return "warning";
	return "default";
};

const isPendingStatus = (statusId, statusNameToIdMap) => {
	const pendingId = statusNameToIdMap.get("PENDING") ?? 3;
	return Number(statusId) === Number(pendingId);
};

const AddAdvancePayment = ({
	onCancel,
	formOnly,
	tableOnly,
	listSource,
	employeeId: employeeIdProp,
	onSuccess,
	showApprovalActions = true,
}) => {
	const dispatch = useDispatch();
	const employeeIdFromToken = getUserFromToken();
	const [form] = Form.useForm();
	const [editingRecord, setEditingRecord] = useState(null);
	const [rejectModalOpen, setRejectModalOpen] = useState(false);
	const [rejectingRecord, setRejectingRecord] = useState(null);
	const [rejectForm] = Form.useForm();
	const [approveModalOpen, setApproveModalOpen] = useState(false);
	const [approvingRecord, setApprovingRecord] = useState(null);
	const [approveForm] = Form.useForm();

	const {
		list = [],
		advancesByReporting = [],
		advancesById = [],
		loading,
	} = useSelector((state) => state.advancePayment || {});
	const { advanceStatusMaster = [] } = useSelector((state) => state.advancePayment || {});
	const { list: employeeList = [] } = useSelector((state) => state.users || {});

	const advanceList =
		listSource === "reporting"
			? advancesByReporting
			: listSource === "self"
			? advancesById
			: list;

	useEffect(() => {
		if (listSource === "reporting" && (employeeIdProp ?? employeeIdFromToken)) {
			dispatch(loadAdvancePaymentsByReporting(employeeIdProp ?? employeeIdFromToken));
		} else if (listSource === "self" && (employeeIdProp ?? employeeIdFromToken)) {
			dispatch(loadAdvancePaymentsById(employeeIdProp ?? employeeIdFromToken));
		}
		dispatch(loadAllStaff({ status: "true" }));
		dispatch(loadAdvancePaymentStatusMaster());
	}, [dispatch, listSource, employeeIdProp, employeeIdFromToken]);

	const statusOptions = useMemo(() => {
		const mappedOptions = Array.isArray(advanceStatusMaster)
			? advanceStatusMaster
					.map((status) => {
						const value = Number(
							status.AdvancePaymentStatusId ?? status.StatusId ?? status.statusId ?? status.id
						);
						const label =
							status.AdvancePaymentStatusName ??
							status.StatusName ??
							status.statusName ??
							status.name;
						if (!Number.isFinite(value) || label === undefined || label === null) {
							return null;
						}
						return { value, label: String(label) };
					})
					.filter(Boolean)
			: [];

		if (mappedOptions.length) {
			return mappedOptions;
		}

		return [
			{ value: 3, label: "Pending" },
			{ value: 1, label: "Approved" },
			{ value: 0, label: "Rejected" },
		];
	}, [advanceStatusMaster]);

	const statusNameToIdMap = useMemo(() => {
		const map = new Map();
		statusOptions.forEach((option) => {
			const normalizedLabel = option.label.toString().trim().toUpperCase();
			if (normalizedLabel) {
				map.set(normalizedLabel, option.value);
			}
		});
		return map;
	}, [statusOptions]);

	const defaultStatusId = useMemo(() => {
		const pendingOption = statusOptions.find(
			(option) => option.label?.toString().trim().toUpperCase() === "PENDING"
		);
		if (pendingOption) {
			return pendingOption.value;
		}
		return statusOptions.length ? statusOptions[0].value : 3;
	}, [statusOptions]);

	const employeeOptions = useMemo(
		() =>
			Array.isArray(employeeList)
				? employeeList
						.map((employee) => {
							const value = Number(employee.EmployeeId ?? employee.id ?? 0);
							const label =
								employee.EmployeeName ??
								[employee.firstName, employee.lastName].filter(Boolean).join(" ").trim();
							if (!value || !label) {
								return null;
							}
							return { value, label };
						})
						.filter(Boolean)
				: [],
		[employeeList]
	);

	const employeeNameMap = useMemo(() => {
		const map = new Map();
		employeeOptions.forEach((option) => map.set(option.value, option.label));
		return map;
	}, [employeeOptions]);

	useEffect(() => {
		if (!editingRecord) {
			form.setFieldsValue({
				EmployeeId: Number(employeeIdFromToken) || null,
				StatusId: form.getFieldValue("StatusId") ?? defaultStatusId,
				Installments: form.getFieldValue("Installments") ?? 1,
			});
		}
	}, [defaultStatusId, editingRecord, employeeIdFromToken, form]);

	const normalizedAdvances = useMemo(
		() =>
			Array.isArray(advanceList)
				? advanceList.map((item, index) => {
						const advancePaymentId = Number(
							item.AdvancePaymentId ??
								item.advancePaymentId ??
								item.Id ??
								item.id ??
								0
						);
						const employeeValue = Number(
							item.EmployeeId ?? item.employeeId ?? employeeIdFromToken ?? 0
						);
						const employeeName =
							item.EmployeeName ??
							item.employeeName ??
							employeeNameMap.get(employeeValue) ??
							"";
						const rawStatus =
							item.StatusId ?? item.statusId ?? item.Status ?? item.status ?? 0;
						const statusId =
							typeof rawStatus === "string"
								? statusStringToId(rawStatus, statusNameToIdMap)
								: Number(rawStatus ?? 0);
						const statusLabel =
							item.AdvancePaymentStatusName ??
							item.StatusName ??
							item.statusName ??
							getStatusLabel(statusId, statusOptions);
						return {
							key: advancePaymentId || index,
							AdvancePaymentId: advancePaymentId,
							EmployeeId: employeeValue,
							EmployeeName: employeeName,
							Amount: Number(item.Amount ?? item.amount ?? 0),
							ApprovedAmount:
								item.ApprovedAmount !== undefined && item.ApprovedAmount !== null
									? Number(item.ApprovedAmount ?? item.approvedAmount)
									: null,
							Installments: Number(item.Installments ?? item.installments ?? 1),
							RequestDate: item.RequestDate ?? item.requestDate ?? null,
							Reason: item.Reason ?? item.reason ?? "",
							Remarks: item.Remarks ?? item.remarks ?? "",
							StatusId: statusId,
							StatusLabel: statusLabel,
							DataStatus: item.DataStatus ?? item.dataStatus ?? 1,
						};
				  })
				: [],
		[advanceList, employeeIdFromToken, employeeNameMap, statusNameToIdMap, statusOptions]
	);

	const handleResetForm = useCallback(() => {
		setEditingRecord(null);
		form.resetFields();
		form.setFieldsValue({
			EmployeeId: Number(employeeIdFromToken) || null,
			StatusId: defaultStatusId,
			Installments: 1,
		});
	}, [defaultStatusId, employeeIdFromToken, form]);

	const handleEditRecord = useCallback(
		(record) => {
			setEditingRecord(record);
			form.setFieldsValue({
				EmployeeId: record.EmployeeId ?? Number(employeeIdFromToken) ?? null,
				Amount: record.Amount ?? null,
				ApprovedAmount: record.ApprovedAmount ?? null,
				Installments: record.Installments ?? 1,
				RequestDate: record.RequestDate ? dayjs(record.RequestDate) : null,
				Reason: record.Reason ?? "",
				Remarks: record.Remarks ?? "",
				StatusId: record.StatusId ?? defaultStatusId,
			});
		},
		[defaultStatusId, employeeIdFromToken, form]
	);

	const refetchList = useCallback(() => {
		if (listSource === "reporting" && (employeeIdProp ?? employeeIdFromToken)) {
			dispatch(loadAdvancePaymentsByReporting(employeeIdProp ?? employeeIdFromToken));
		} else if (listSource === "self" && (employeeIdProp ?? employeeIdFromToken)) {
			dispatch(loadAdvancePaymentsById(employeeIdProp ?? employeeIdFromToken));
		}
	}, [dispatch, listSource, employeeIdProp, employeeIdFromToken]);

	const handleDeleteRecord = useCallback(
		async (record) => {
			const payload = {
				AdvancePaymentId: record.AdvancePaymentId,
				EmployeeId: record.EmployeeId || Number(employeeIdFromToken) || 0,
				Amount: record.Amount ?? 0,
				ApprovedAmount: record.ApprovedAmount ?? null,
				Installments: record.Installments ?? 1,
				RequestDate: record.RequestDate
					? dayjs(record.RequestDate).format(DATE_FORMAT)
					: null,
				Reason: record.Reason ?? null,
				Remarks: record.Remarks ?? null,
				StatusId: record.StatusId ?? defaultStatusId,
				DataStatus: 2,
			};
			const response = await dispatch(saveAdvancePayment(payload));
			if (response?.meta?.requestStatus !== "rejected") {
				refetchList();
				if (editingRecord?.AdvancePaymentId === record.AdvancePaymentId) {
					handleResetForm();
				}
			}
		},
		[
			defaultStatusId,
			dispatch,
			editingRecord,
			employeeIdFromToken,
			handleResetForm,
			refetchList,
		]
	);

	const onFinish = async (values) => {
		const selectedEmployeeId =
			values.EmployeeId !== undefined && values.EmployeeId !== null
				? Number(values.EmployeeId)
				: Number(employeeIdFromToken);

		const payload = {
			AdvancePaymentId: editingRecord?.AdvancePaymentId ?? 0,
			EmployeeId: selectedEmployeeId,
			Amount:
				values.Amount !== undefined && values.Amount !== null ? Number(values.Amount) : 0,
			ApprovedAmount:
				values.ApprovedAmount !== undefined && values.ApprovedAmount !== null
					? Number(values.ApprovedAmount)
					: null,
			Installments:
				values.Installments !== undefined && values.Installments !== null
					? Number(values.Installments)
					: 1,
			RequestDate: values.RequestDate ? dayjs(values.RequestDate).format(DATE_FORMAT) : null,
			Reason: values.Reason ?? null,
			Remarks: values.Remarks ?? null,
			StatusId: values.StatusId ?? defaultStatusId,
			DataStatus: editingRecord?.DataStatus ?? 1,
		};

		const response = await dispatch(saveAdvancePayment(payload));
		if (response?.meta?.requestStatus !== "rejected") {
			refetchList();
			onSuccess?.();
			handleResetForm();
			onCancel?.();
		}
	};

	const onFinishFailed = () => {
		toast.warning("Please review the form for errors before submitting");
	};

	const openApproveModal = useCallback(
		(record) => {
			setApprovingRecord(record);
			approveForm.setFieldsValue({
				ApprovedAmount: record.Amount ?? null,
			});
			setApproveModalOpen(true);
		},
		[approveForm]
	);

	const handleApproveSubmit = async () => {
		try {
			const values = await approveForm.validateFields();
			const advancePaymentId =
				approvingRecord?.AdvancePaymentId ??
				approvingRecord?.advancePaymentId ??
				approvingRecord?.id;
			if (!advancePaymentId) return;

			const approvedStatusId = statusNameToIdMap.get("APPROVED") ?? 1;
			const result = await dispatch(
				advancePaymentUpdateAction({
					AdvancePaymentId: advancePaymentId,
					StatusId: approvedStatusId,
					ApprovedAmount: values.ApprovedAmount,
				})
			);
			if (result?.meta?.requestStatus === "fulfilled") {
				setApproveModalOpen(false);
				setApprovingRecord(null);
				approveForm.resetFields();
				refetchList();
			}
		} catch (_error) {
			// validation failed
		}
	};

	const openRejectModal = useCallback(
		(record) => {
			setRejectingRecord(record);
			rejectForm.resetFields();
			setRejectModalOpen(true);
		},
		[rejectForm]
	);

	const handleRejectSubmit = async () => {
		try {
			const values = await rejectForm.validateFields();
			const advancePaymentId =
				rejectingRecord?.AdvancePaymentId ??
				rejectingRecord?.advancePaymentId ??
				rejectingRecord?.id;
			if (!advancePaymentId) return;

			const rejectedStatusId = statusNameToIdMap.get("REJECTED") ?? 0;
			const result = await dispatch(
				advancePaymentUpdateAction({
					AdvancePaymentId: advancePaymentId,
					StatusId: rejectedStatusId,
					ApprovedAmount: null,
					Remarks: values.Remarks ?? null,
				})
			);
			if (result?.meta?.requestStatus === "fulfilled") {
				setRejectModalOpen(false);
				setRejectingRecord(null);
				rejectForm.resetFields();
				refetchList();
			}
		} catch (_error) {
			// validation failed
		}
	};

	const columns = useMemo(
		() => [
			{
				title: "Request Date",
				dataIndex: "RequestDate",
				key: "RequestDate",
				render: (value) => (value ? dayjs(value).format(DATE_FORMAT) : "-"),
			},
			{
				title: "Employee",
				dataIndex: "EmployeeName",
				key: "EmployeeName",
				render: (_, record) =>
					record.EmployeeName || employeeNameMap.get(record.EmployeeId) || "-",
			},
			{
				title: "Amount",
				dataIndex: "Amount",
				key: "Amount",
				render: (value) =>
					value !== undefined && value !== null ? Number(value).toFixed(2) : "0.00",
			},
			{
				title: "Approved Amount",
				dataIndex: "ApprovedAmount",
				key: "ApprovedAmount",
				render: (value) =>
					value !== undefined && value !== null ? Number(value).toFixed(2) : "-",
			},
			{
				title: "Installments",
				dataIndex: "Installments",
				key: "Installments",
				render: (value) => value ?? "-",
			},
			{
				title: "Reason",
				dataIndex: "Reason",
				key: "Reason",
				ellipsis: true,
				render: (value) => value || "-",
			},
			{
				title: "Status",
				dataIndex: "StatusId",
				key: "StatusId",
				render: (_, record) => (
					<Tag color={getStatusColor(record.StatusLabel)}>
						{record.StatusLabel ?? getStatusLabel(record.StatusId, statusOptions)}
					</Tag>
				),
			},
			{
				title: "Remarks",
				dataIndex: "Remarks",
				key: "Remarks",
				ellipsis: true,
				render: (value) => value || "-",
			},
			{
				title: "Actions",
				key: "actions",
				render: (_, record) => {
					const pending = isPendingStatus(record.StatusId, statusNameToIdMap);
					return (
						<Space>
							{showApprovalActions && pending ? (
								<>
									<Tooltip title="Approve">
										<Button
											type="text"
											icon={<CheckOutlined style={{ color: "#16a34a" }} />}
											onClick={() => openApproveModal(record)}
										/>
									</Tooltip>
									<Tooltip title="Reject">
										<Button
											type="text"
											icon={<CloseOutlined style={{ color: "#dc2626" }} />}
											onClick={() => openRejectModal(record)}
										/>
									</Tooltip>
								</>
							) : null}
							{listSource === "self" && pending ? (
								<>
									<Tooltip title="Edit">
										<Button
											type="text"
											icon={<EditOutlined />}
											onClick={() => handleEditRecord(record)}
										/>
									</Tooltip>
									<Popconfirm
										title="Delete advance payment request?"
										onConfirm={() => handleDeleteRecord(record)}
										okText="Yes"
										cancelText="No"
									>
										<Tooltip title="Delete">
											<Button type="text" danger icon={<DeleteOutlined />} />
										</Tooltip>
									</Popconfirm>
								</>
							) : null}
						</Space>
					);
				},
			},
		],
		[
			employeeNameMap,
			handleDeleteRecord,
			handleEditRecord,
			openApproveModal,
			openRejectModal,
			showApprovalActions,
			listSource,
			statusNameToIdMap,
			statusOptions,
		]
	);

	return (
		<div className="add-advance-payment-container">
			<Row className="advance-payment-row" gutter={[16, 16]}>
				{!tableOnly && (
					<Col
						xs={24}
						sm={24}
						md={formOnly ? 24 : 10}
						lg={formOnly ? 24 : 10}
						xl={formOnly ? 24 : 9}
						className="column-design border rounded card-custom advance-payment-form-col"
					>
						<Title level={4} className="m-2 mt-2 mb-5 text-center">
							{editingRecord ? "Update Advance Payment" : "Apply for Advance Payment"}
						</Title>
						<Form
							form={form}
							style={{ marginBottom: "40px" }}
							name="advance-payment-form"
							labelCol={{ span: 8 }}
							wrapperCol={{ span: 14 }}
							onFinish={onFinish}
							onFinishFailed={onFinishFailed}
							autoComplete="off"
						>
							<Form.Item
								style={{ marginBottom: "10px" }}
								label="Employee"
								name="EmployeeId"
								rules={[{ required: true, message: "Please select an employee" }]}
							>
								<Select
									placeholder="Select an employee"
									options={employeeOptions}
									allowClear
									showSearch
									optionFilterProp="label"
									disabled={Boolean(employeeIdFromToken)}
								/>
							</Form.Item>

							<Form.Item
								style={{ marginBottom: "10px" }}
								label="Amount"
								name="Amount"
								rules={[
									{ required: true, message: "Please enter the advance amount" },
									{
										type: "number",
										min: 1,
										message: "Amount must be greater than 0",
									},
								]}
							>
								<InputNumber
									style={{ width: "100%" }}
									min={1}
									step={100}
									placeholder="Enter advance amount"
									formatter={(value) =>
										value ? `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
									}
									parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
								/>
							</Form.Item>

							<Form.Item
								style={{ marginBottom: "10px" }}
								label="Installments"
								name="Installments"
								initialValue={1}
								rules={[
									{ required: true, message: "Please enter number of installments" },
									{
										type: "number",
										min: 1,
										max: 24,
										message: "Installments must be between 1 and 24",
									},
								]}
							>
								<InputNumber
									style={{ width: "100%" }}
									min={1}
									max={24}
									placeholder="Number of installments"
								/>
							</Form.Item>

							<Form.Item
								style={{ marginBottom: "10px" }}
								label="Request Date"
								name="RequestDate"
								initialValue={dayjs()}
								rules={[{ required: true, message: "Please select request date" }]}
							>
								<DatePicker style={{ width: "100%" }} format={DATE_FORMAT} />
							</Form.Item>

							<Form.Item
								style={{ marginBottom: "10px" }}
								label="Reason"
								name="Reason"
								rules={[{ required: true, message: "Please enter reason for advance" }]}
							>
								<Input.TextArea rows={3} placeholder="Enter reason for advance payment" />
							</Form.Item>

							<Form.Item style={{ marginBottom: "10px" }} label="Remarks" name="Remarks">
								<Input.TextArea rows={2} placeholder="Additional remarks (optional)" />
							</Form.Item>

							<Form.Item wrapperCol={{ offset: 8, span: 14 }}>
								<Space>
									<Button type="primary" size="large" htmlType="submit" loading={loading}>
										{editingRecord ? "Update Request" : "Submit Request"}
									</Button>
									{editingRecord ? (
										<Button onClick={handleResetForm} disabled={loading}>
											Cancel
										</Button>
									) : null}
								</Space>
							</Form.Item>
						</Form>
					</Col>
				)}

				{(tableOnly || !formOnly) && (
					<Col
						xs={24}
						sm={24}
						md={tableOnly ? 24 : 14}
						lg={tableOnly ? 24 : 14}
						xl={tableOnly ? 24 : 15}
						className="advance-payment-table-col"
					>
						<div className="advance-payment-table-wrapper">
							<Table
								dataSource={normalizedAdvances}
								columns={columns}
								loading={loading}
								rowKey="key"
								pagination={{ pageSize: 10 }}
								className="column-design border rounded card-custom"
								scroll={{ x: true }}
							/>
						</div>
					</Col>
				)}
			</Row>

			<Modal
				title="Approve Advance Payment"
				open={approveModalOpen}
				onCancel={() => {
					setApproveModalOpen(false);
					setApprovingRecord(null);
					approveForm.resetFields();
				}}
				onOk={handleApproveSubmit}
				okText="Approve"
				cancelText="Cancel"
				confirmLoading={loading}
				destroyOnClose
			>
				<Form form={approveForm} layout="vertical">
					<p>
						Approving advance payment request for{" "}
						<strong>
							{approvingRecord?.EmployeeName ||
								employeeNameMap.get(approvingRecord?.EmployeeId) ||
								"employee"}
						</strong>{" "}
						— requested amount:{" "}
						<strong>₹{Number(approvingRecord?.Amount ?? 0).toFixed(2)}</strong>
					</p>
					<Form.Item
						label="Approved Amount"
						name="ApprovedAmount"
						rules={[
							{ required: true, message: "Please enter approved amount" },
							{
								type: "number",
								min: 1,
								message: "Approved amount must be greater than 0",
							},
						]}
					>
						<InputNumber
							style={{ width: "100%" }}
							min={1}
							step={100}
							placeholder="Enter approved amount"
						/>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="Reject Advance Payment"
				open={rejectModalOpen}
				onCancel={() => {
					setRejectModalOpen(false);
					setRejectingRecord(null);
					rejectForm.resetFields();
				}}
				onOk={handleRejectSubmit}
				okText="Reject"
				okButtonProps={{ danger: true }}
				cancelText="Cancel"
				confirmLoading={loading}
				destroyOnClose
			>
				<Form form={rejectForm} layout="vertical">
					<p>
						Rejecting advance payment request for{" "}
						<strong>
							{rejectingRecord?.EmployeeName ||
								employeeNameMap.get(rejectingRecord?.EmployeeId) ||
								"employee"}
						</strong>{" "}
						— amount: <strong>₹{Number(rejectingRecord?.Amount ?? 0).toFixed(2)}</strong>
					</p>
					<Form.Item
						label="Rejection Remarks"
						name="Remarks"
						rules={[{ required: true, message: "Please enter rejection reason" }]}
					>
						<Input.TextArea rows={3} placeholder="Enter reason for rejection" />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default AddAdvancePayment;
